import Bouncer from 'formbouncerjs';
import i18next from 'i18next';
import { $ } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';
import shouldBlockEnterOnSelectionControl from '@utils/ShouldBlockEnterOnSelectionControl';

let validator;

function init() {
  if ($('[data-au-form-validate]')) {
    formValidate();
    bindEnterSubmitBehavior();
  }

  if ($('[data-au-form-on-send-status]')) {
    formDatalayerOnSendStatus();
  }
}

function bindEnterSubmitBehavior() {
  document.querySelectorAll('[data-au-form-validate]').forEach((form) => {
    if (form.dataset.auEnterSubmitBound === 'true') return;
    form.dataset.auEnterSubmitBound = 'true';

    form.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      if (event.defaultPrevented) return;
      if (!(event.target instanceof HTMLElement)) return;
      if (event.target.tagName === 'TEXTAREA') return;
      if (event.target.isContentEditable) return;
      if (form.hasAttribute('data-au-emergent-form-form')) return;

      if (shouldBlockEnterOnSelectionControl(event.target)) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
        return;
      }

      form.querySelector('[type="submit"]')?.click();
    });
  });
}

function formValidate() {
  validator = new Bouncer('[data-au-form-validate]', {
    // Classes & IDs
    fieldClass: 'au-form-field-error', // Applied to fields with errors
    errorClass: 'au-form-msg-error t-detail', // Applied to the error message for invalid fields
    fieldPrefix: 'au-bouncer-field-', // If a field doesn't have a name or ID, one is generated with this prefix
    errorPrefix: 'au-bouncer-error-', // Prefix used for error message IDs

    // Patterns
    // Validation patterns for specific input types
    patterns: {
      email:
        // eslint-disable-next-line no-control-regex
        /^([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x22([^\x0d\x22\x5c\x80-\xff]|\x5c[\x00-\x7f])*\x22))*\x40([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d)(\x2e([^\x00-\x20\x22\x28\x29\x2c\x2e\x3a-\x3c\x3e\x40\x5b-\x5d\x7f-\xff]+|\x5b([^\x0d\x5b-\x5d\x80-\xff]|\x5c[\x00-\x7f])*\x5d))*(\.\w{2,})+$/,
      url: /^(?:(?:https?|HTTPS?|ftp|FTP):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-zA-Z\u00a1-\uffff0-9]-*)*[a-zA-Z\u00a1-\uffff0-9]+)(?:\.(?:[a-zA-Z\u00a1-\uffff0-9]-*)*[a-zA-Z\u00a1-\uffff0-9]+)*(?:\.(?:[a-zA-Z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/,
      number: /[-+]?[0-9]*[.,]?[0-9]+/,
      color: /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/,
      date: /(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1[0-9]|2[0-9])|(?:(?!02)(?:0[1-9]|1[0-2])-(?:30))|(?:(?:0[13578]|1[02])-31))/,
      time: /(0[0-9]|1[0-9]|2[0-3])(:[0-5][0-9])/,
      month: /(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2]))/,
    },

    // Message Settings
    messageAfterField: true, // If true, displays error message below field. If false, displays it above.
    messageCustom: 'data-au-bouncer-message', // The data attribute to use for customer error messages
    messageTarget: 'data-au-bouncer-target', // The data attribute to pass in a custom selector for the field error location

    // Error messages by error type
    messages: {
      missingValue: {
        checkbox: () => i18next.t('required'),
        radio: i18next.t('radio'),
        select: i18next.t('select'),
        'select-multiple': i18next.t('select-multiple'),
        default: () => i18next.t('required'),
      },
      patternMismatch: {
        email: i18next.t('emailMismatch'),
        url: i18next.t('urlMismatch'),
        number: i18next.t('numberMismatch'),
        color: i18next.t('colorMismatch'),
        date: i18next.t('dateMismatch'),
        time: i18next.t('timeMismatch'),
        month: i18next.t('monthMismatch'),
        default: i18next.t('defaultMismatch'),
      },
      outOfRange: {
        over: i18next.t('over'),
        under: i18next.t('under'),
      },
      wrongLength: {
        over: i18next.t('overLength'),
        under: i18next.t('underLength'),
      },
      checkboxSet: i18next.t('checkboxSet'),
      valueMismatch(field) {
        const customMessage = field.getAttribute('data-au-bouncer-mismatch-message');
        return customMessage ? customMessage : i18next.t('valueMismatch');
      },
      helpPatternMismatch: i18next.t('helpPatternMismatch'),
      calendarMismatch: i18next.t('calendarMismatch'),
      phoneValidation: i18next.t('phoneValidation'),
    },

    // Form Submission
    disableSubmit: false, // If true, native form submission is suppressed even when form validates

    // Custom Events
    emitEvents: true, // If true, emits custom events,
    customValidations: {
      checkboxSet(field) {
        // where at least one checkbox must be checked
        const wrapper = field.closest('[data-au-bouncer-required-set]');

        if (!wrapper) return false;
        const checkboxes = wrapper.querySelectorAll('[type="checkbox"]');
        const checkboxError = wrapper.querySelector('[data-au-form-group-error]');
        if (checkboxes.length > 0) {
          let checkedCount = 0;
          checkboxes.forEach(function (checkbox) {
            if (checkbox.checked) {
              checkedCount++;
            }
          });
          if (checkedCount === 0) {
            checkboxes.forEach(function (checkbox) {
              checkbox.classList.add('au-form-field-error');
            });
            checkboxError.classList.remove('au-hide');
            return true;
          }
          checkboxes.forEach(function (checkbox) {
            checkbox.classList.remove('au-form-field-error');
          });
          checkboxError.classList.add('au-hide');
          return false;
        }
      },
      valueMismatch(field) {
        // Look for a selector for a field to compare
        // If there isn't one, return false (no error)
        const selector = field.getAttribute('data-au-bouncer-match');
        if (!selector) return false;

        // Get the field to compare
        const otherField = field.form.querySelector(selector);
        if (!otherField) return false;

        // Compare the two field values
        // We use a negative comparison here because if they do match, the field validates
        // We want to return true for failures, which can be confusing
        return otherField.value !== field.value;
      },
      helpPatternMismatch(field) {
        const fieldEl = field.closest('[data-au-form-element]');
        if (!fieldEl) return false;
        const inputError = fieldEl.querySelector('[data-au-input-base-error]');
        const fieldHelpPatterns = field.closest('[data-au-form-element]').querySelectorAll('[data-au-form-pattern]');
        if (!fieldHelpPatterns.length) return false;
        let allHelpPatternsOk;
        fieldHelpPatterns.forEach((pat) => {
          if (pat.classList.contains('au-form-help-state-texts__text--ko')) {
            allHelpPatternsOk = false;
            inputError.classList.add('au-hide');
          } else {
            allHelpPatternsOk = true;
            inputError.classList.remove('au-hide');
          }
        });

        if (allHelpPatternsOk) return false;

        return true; // All patterns are valid
      },
      calendarMismatch(field) {
        // Check if the field has the data-au-datepicker attribute
        if (!field.closest('[data-au-datepicker]')) return false;

        // Regex to match the dd/mm/yyyy pattern
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/;

        // Test the field value against the regex
        if (dateRegex.test(field.value)) return false;

        return true;
      },
      phoneValidation(field) {
        // Check if the field has the data-au-input-phone-number attribute
        if (!field.closest('[data-au-input-phone-number]')) return false;

        // Regex to match the dd/mm/yyyy pattern
        const dateRegex = /^\d{9,15}$/;

        // Test the field value against the regex
        if (dateRegex.test(field.value)) return false;

        return true;
      },
    },
  });

  // Detect show error events
  document.addEventListener(
    'bouncerShowError',
    function (event) {
      // The field with the error
      var field = event.target;

      if (field.closest('[data-au-form-element]')) {
        field.closest('[data-au-form-element]').classList.add('au-form-element-error');
      }
    },
    false,
  );

  // Detect hide error events
  document.addEventListener(
    'bouncerRemoveError',
    function (event) {
      // The field with the error
      var field = event.target;
      if (field.closest('[data-au-form-element]')) {
        field.closest('[data-au-form-element]').classList.remove('au-form-element-error');
      }
    },
    false,
  );

  // Detect succesfully validated form
  document.addEventListener(
    'bouncerFormValid',
    function (event) {
      let closestSelector;
      if (event.target.closest('[data-au-form-block]')) {
        closestSelector = '[data-au-form-block]';
      } else if (event.target.closest('section')) {
        closestSelector = 'section';
      } else {
        closestSelector = null;
      }

      if (!closestSelector) return false;
      let formTitleText;

      if (event.target.closest(closestSelector).querySelector('[data-au-form-title]')) {
        formTitleText = removeAccentMarkAndUppercase(
          event.target.closest(closestSelector).querySelector('[data-au-form-title]').innerText,
        );
      } else {
        formTitleText = '';
      }

      const clickText = removeAccentMarkAndUppercase(
        event.target.querySelector('[type="submit"]').querySelector(':not([data-au-icon])').innerText,
      );

      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickBoton',
        item_id: `${formTitleText}_${clickText}`,
      });
    },
    false,
  );
}

function formDatalayerOnSendStatus() {
  $('[data-au-form-on-send-status]')
    .querySelectorAll('a')
    .forEach((link) => {
      link.addEventListener('click', (e) => {
        const statusTitle = removeAccentMarkAndUppercase(
          e.target.closest('[data-au-form-on-send-status]').querySelector('[data-au-form-on-send-status-title]')
            .innerText,
        );
        const linkText = removeAccentMarkAndUppercase(e.target.querySelector(':not([data-au-icon])').innerText);
        const item_id = `${statusTitle}_${linkText}`;
        let content_type;

        if (link.hasAttribute('data-au-form-on-send-status-btn')) {
          content_type = 'clickBoton';
        } else {
          content_type = e.target.hasAttribute('download') ? 'clickDescarga' : 'clickEnlace';
        }

        window.dataLayer.push({
          event: 'navigation',
          content_type,
          item_id,
        });
      });
    });
}

export default { init, getValidator: () => validator };
